import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";
import PreviewIcon from "@mui/icons-material/Preview";
import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {
  MaskedText,
  DatePicker, MaskedNumber, ComboSelect, FormSelect,
} from "../../../controls";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import CostingUuidLookup from "../../Costings/components/CostingLookup";
import TripUuidLookup from "../../Trips/components/TripUuidLookup";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import EnquiryActivityValidator from "../validators/EnauiryActivityValidator";
import { useEnquiryActivities, useAddEnquiryActivity, useDeleteEnquiryActivity, useUpdateEnquiryActivity } from "../../../services/enquiry";
import NotesActionButton from "../../../controls/Notes/NotesActionButton";
import NotesDialog from "../../../controls/Notes/NotesDialog";
import {QuoteUuidLookup} from "./QuoteUuidLookup";


const apiSitePrefix = getApiSitePrefix();
const AccessControl = require("../../../AccessControl");

const dateFormat = "DD/MM/YYYY HH:mm";
const today = moment().toISOString();
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}


const getUserName = () => {
  const userInfo = AccessControl.getUserInfo();
  if (userInfo && "firstName" in userInfo && "lastName" in userInfo)
    return (userInfo.firstName + " " + userInfo.lastName);
  else
    return "";
}

const EnquiryActivityList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const validatorColumn = ValidatorColumn(EnquiryActivityValidator, true);

  const props = {
    ..._props
  }
  const departmentUsers = props.departmentUsers;
  const userOptions = props.userOptions;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    currentUser: getUserName()
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddEnquiryActivity = useAddEnquiryActivity();
  const handleUpdateEnquiryActivity = useUpdateEnquiryActivity();
  const handleDeleteEnquiryActivity = useDeleteEnquiryActivity();

  const queryClient = useQueryClient();
  const extraKeys = [props.enquiryUuid];
  const activities = useEnquiryActivities({tableRef, queryClient, extraKeys});

  const getCosting = async (costingUuid) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/costings/list/${costingUuid}`);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }

      if (!response.data) {
        const message = `Could not retrieve Costing. Server is not responding`;
        console.log(message);
        SystemData.alert("Error", message);
      } else if (!Array.isArray(response.data) || response.data.length < 1) {
        const message = `Could not retrieve Costing. Costing data not found`;
        console.log(message);
        SystemData.alert("Error", message);
      }
    } catch (err) {
      const message = `Could not retrieve Costing. Server is not responding`;
      console.log(message);
      SystemData.alert("Error", message);
    }

    return null;
  }

  const viewCosting = async (associatedCostingUuid) => {
    if (associatedCostingUuid) {
      const costingRecord = await getCosting(associatedCostingUuid);
      if (costingRecord) {
        // eventBus.trigger("EditCostingDialog.open", costingRecord);
        eventBus.trigger("ViewCostingDialog.open", costingRecord);
      }
    }
  }

  const getQuote = async (quoteUuid) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/quotes/list/${quoteUuid}`);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }

      if (!response.data) {
        const message = `Could not retrieve Quote. Server is not responding`;
        console.log(message);
        SystemData.alert("Error", message);
      } else if (!Array.isArray(response.data) || response.data.length < 1) {
        const message = `Could not retrieve Quote. Quote data not found`;
        console.log(message);
        SystemData.alert("Error", message);
      }
    } catch (err) {
      const message = `Could not retrieve Quote. Server is not responding`;
      console.log(message);
      SystemData.alert("Error", message);
    }

    return null;
  }

  const viewQuote = async (associatedQuoteUuid) => {
    if (associatedQuoteUuid) {
      const quoteRecord = await getQuote(associatedQuoteUuid);
      if (quoteRecord) {
        eventBus.trigger("ViewQuoteDialog.open", {...quoteRecord, departmentUsers});
      }
    }
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        title: "Date",
        field: "activityDate",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <span>{moment(rowData.activityDate).format(dateFormat)}</span>
        )
      },
      {
        ...baseColumn,
        // ...ColumnWidth(120, 8, undefined, undefined, undefined, undefined, false),
        title: "Activity",
        field: "activityType",
        initialEditValue: "",
        editComponent: (props) => (
          <ComboSelect
            id="activityType"
            group="ENQUIRIES"
            subGroup="ACTIVITY"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            id="activityType"
            group="ENQUIRIES"
            subGroup="ACTIVITY"
            fullWidth
            value={rowData.activityType}
            displayOnly={true}
          />
        ),
      },
      {
        ...ColumnWidth(25),
        ...baseColumn,
      },
      {
        ...baseColumn,
        ...ColumnWidth(400, 8, undefined, undefined, undefined, undefined, false),
        title: "Associated Costing",
        field: "associatedCostingUuid",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(40)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData) => (
          <div style={{display: "flex"}}>
            <CostingUuidLookup
              value={rowData.associatedCostingUuid}
              type="render"
              hideCode={false}
            />
            <PreviewIcon
              fontSize="medium"
              style={{marginLeft: 5, marginTop: -3, color: theme.palette.primary.main }}
              onClick={async ()=> {
                await viewCosting(rowData.associatedCostingUuid);
              }}
            />
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Total Costs",
        field: "requiredRevenue",
        editable: "always",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={2}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.requiredRevenue}
              prefix="$ "
              allowNegative={false}
            />
          </div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Total Revenue",
        field: "totalRevenue",
        editable: "always",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={2}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.totalRevenue}
              prefix="$ "
              allowNegative={false}
            />
          </div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Profit",
        field: "profit",
        editable: "always",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={2}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.profit}
              prefix="$ "
              allowNegative={false}
            />
          </div>
        )
      },
      {
        ...baseColumn,
        title: "Associated Quote",
        field: "associatedQuoteUuid",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(40)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData) => (
          <div style={{display: "flex"}}>
            <QuoteUuidLookup
              value={rowData.associatedQuoteUuid}
              type="render"
            />
            <PreviewIcon
              fontSize="medium"
              style={{marginLeft: 5, marginTop: -3, color: theme.palette.primary.main }}
              // color={!defaultValues ? 'disabled' : 'primary'}
              onClick={async ()=> {
                await viewQuote(rowData.associatedQuoteUuid);
              }}
            />
          </div>
        ),
      },
      {
        ...baseColumn,
        title: "Associated Trip",
        field: "associatedTripUuid",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(40)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData) => (
          <TripUuidLookup
            value={rowData.associatedTripUuid}
            type="render"
            includeScratchPads={true}
          />
        ),
      },
      {
        ...ColumnWidth(25),
        ...baseColumn,
      },
      {
        ...baseColumn,
        // ...ColumnWidth(120, 8, undefined, undefined, undefined, undefined, false),
        title: "Status",
        field: "status",
        initialEditValue: "",
        editComponent: (props) => (
          <ComboSelect
            id="status"
            group="ENQUIRIES"
            subGroup="ACTIVITY STATUS"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            id="status"
            group="ENQUIRIES"
            subGroup="ACTIVITY STATUS"
            fullWidth
            value={rowData.status}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        title: "Entered By",
        field: "enteredBy",
        initialEditValue: state.currentUser,
        editable: "never",
        editComponent: (props) => (
          <FormSelect
            id="enteredBy"
            options={userOptions}
            onChange={props.onChange}
            value={props.value}
          />
        ),
        render: (rowData) => {
          const userInfo = __.find(departmentUsers?.data, {user_id: rowData.enteredBy});
          return (userInfo ? <span>{`${userInfo.firstName} ${userInfo.lastName}`}</span> : <></>)
        }
      },
    ];
  };

  return (isAuthenticated &&
      <div style={{marginLeft: 50}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              backgroundColor: "#fdfdfd",
              border: "2px solid #aaaaaa",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 5,
              detailPanelType: "single",
              addRowPosition: "first",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            actions={[
              (rowData) => (
                {
                  hidden: false,
                  icon: () => (
                    <NotesActionButton
                      canEdit={true}
                      primaryObjectUuid={rowData.enquiryActivityUuid}
                      noteType="ENQUIRY:ACTIVITY"
                      title="Activity Note"
                      eventPrefix="EnquiryActivityNoteDialog"
                      style={{ color: theme.palette.primary.main, padding: 0 }}
                    />
                  ),
                  iconProps: { style: { color: theme.palette.primary.main }},
                  isFreeAction: false,
                }
              ),
            ]}
            title="Enquiry Activities"
            columns={getColumns({canEdit: state.canEdit})}
            data={activities.data}
            editable={{
              isEditable: () => {
                return false;
              },
              isDeletable: () => {
                return false;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddEnquiryActivity.mutate({record: {...newData, aircraftUuid: props.aircraftUuid}, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateEnquiryActivity.mutate({record: {...newData, aircraftUuid: props.aircraftUuid}, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteEnquiryActivity.mutate({oldRecord: {...oldData, aircraftUuid: props.aircraftUuid}, resolve, reject, extraKeys});
                  }),
            }}
        />

        <NotesDialog
          eventPrefix="EnquiryActivityNoteDialog"
          afterSave={undefined}
          onClose={undefined}
          classes={{}}
        />

      </div>
  );

}

export default EnquiryActivityList;
